import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { Spinner } from "@/components/Spinner/Spinner";

import { useAppSelector } from "@/redux/hooks";

import { liSnPreviewSelector } from "@/pages/Private/redux/liSn/liSn.slice";

import { LiSnPreviewItem } from "@/pages/Private/pages/LiSn/schema/liSn";

interface LiSnPreviewModalProps {
	isOpened: boolean;
	handleClose: () => void;
	isLoading: boolean;
	errorMessage?: string;
}

const LiSnPreviewCard: FunctionComponent<{ item: LiSnPreviewItem }> = ({ item }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liSn.preview.${key}`);
	const picture = item.profile_picture_url_large || item.profile_picture_url;
	const networkDistanceLabel = item.network_distance
		? t(`liSn.preview.networkDistance.${item.network_distance}`, {
				defaultValue: item.network_distance,
			})
		: null;

	return (
		<div className="flex gap-3 p-3 rounded-lg border border-border-light bg-brand-white">
			<div className="shrink-0 w-14 h-14 rounded-full overflow-hidden bg-brand-white border border-border-light flex items-center justify-center">
				{picture ? (
					<img
						alt={item.name}
						className="w-full h-full object-cover"
						src={picture}
						onError={(e) => {
							(e.currentTarget as HTMLImageElement).style.display = "none";
						}}
					/>
				) : (
					<span className="text-xs text-text-meta">{ts("noPicture")}</span>
				)}
			</div>
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2">
					<div className="text-sm text-text font-medium truncate">{item.name}</div>
					{networkDistanceLabel && (
						<span className="text-xs text-text-meta px-2 py-0.5 rounded-full border border-border-light">
							{networkDistanceLabel}
						</span>
					)}
				</div>
				{item.headline && (
					<div className="text-xs text-text-meta mt-0.5 truncate">{item.headline}</div>
				)}
				<div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-text-meta">
					{item.location && <span>{item.location}</span>}
					{typeof item.shared_connections_count === "number" && (
						<span>
							{ts("sharedConnections")}: {item.shared_connections_count}
						</span>
					)}
					{item.keywords_match && <span>{item.keywords_match}</span>}
				</div>
				{item.public_profile_url && (
					<a
						className="text-xs text-blue-action mt-1 inline-block"
						href={item.public_profile_url}
						rel="noreferrer"
						target="_blank"
					>
						{ts("viewOnLinkedin")}
					</a>
				)}
			</div>
		</div>
	);
};

export const LiSnPreviewModal: FunctionComponent<LiSnPreviewModalProps> = ({
	isOpened,
	handleClose,
	isLoading,
	errorMessage,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liSn.preview.${key}`);
	const preview = useAppSelector(liSnPreviewSelector);

	return (
		<Modal
			cancelButtonText={t("basics.close")}
			handleClose={handleClose}
			isOpened={isOpened}
			size="md"
			title={ts("title")}
		>
			<div className="flex flex-col gap-3 min-h-[120px]">
				{isLoading && (
					<div className="flex items-center justify-center py-10">
						<Spinner color="#4D66F5" />
					</div>
				)}

				{!isLoading && errorMessage && (
					<div className="text-sm text-red-500 p-3 rounded-lg border border-border-light bg-brand-white">
						{errorMessage}
					</div>
				)}

				{!isLoading && !errorMessage && preview && (
					<>
						{preview.message && (
							<div className="text-xs text-text p-3 rounded-lg border border-border-light bg-brand-white">
								<div className="font-medium mb-1">{ts("fallbackTitle")}</div>
								<div className="text-text-meta">{preview.message}</div>
							</div>
						)}

						<div className="text-sm text-text-meta">
							{t("liSn.preview.resultsCount", {
								shown: preview.items.length,
								total: preview.totalCount,
							})}
						</div>

						{preview.items.length === 0 ? (
							<div className="text-sm text-text-meta py-6 text-center">{ts("empty")}</div>
						) : (
							<div className="grid grid-cols-1 gap-2">
								{preview.items.map((item) => (
									<LiSnPreviewCard key={item.id} item={item} />
								))}
							</div>
						)}
					</>
				)}
			</div>
		</Modal>
	);
};
